import type {
  MarketingTargetConfig,
  MarketingTargetMetric,
  MarketingTargetStatus,
} from '@/api/modules/marketingTarget'

/**
 * 系统默认喜报背景地址；由产品提供基础和冲刺两条可访问 HTTPS 链接。
 * 链接未配置时，界面保留红金渐变降级，不影响自定义图片和喜报生命周期。
 */
export const targetCelebrationDefaultImageUrls: Record<'base' | 'sprint', string> = {
  base: 'https://x-img.yaakoo123.cn/biz/public-assets/logo/2026/09/07005cd3-6b4f-4406-ad93-bf4845d93b16.png',
  sprint:
    'https://x-img.yaakoo123.cn/biz/public-assets/license/2026/09/b9eb24db-501a-48c7-84fe-1b52f0ba9eae.png',
}

export const metricLabels: Record<MarketingTargetMetric, string> = {
  net_sales: '净销售额',
  // valid_orders: '有效订单数',
  // first_orders: '首购订单数',
  // repeat_orders: '复购订单数',
  // product_quantity: '商品件数',
  // conversion_rate: '加粉成交率',
}
export const metricUnits: Record<MarketingTargetMetric, string> = {
  net_sales: '元',
  valid_orders: '单',
  first_orders: '单',
  repeat_orders: '单',
  product_quantity: '件',
  conversion_rate: '%',
}
export const statusLabels: Record<MarketingTargetStatus, string> = {
  draft: '草稿',
  pending_approval: '待审批',
  pending_start: '待开始',
  active: '进行中',
  cooling: '冷静期',
  pending_settlement: '待结算',
  completed: '已完成',
  cancelled: '已取消',
}
export const metricOptions = Object.entries(metricLabels).map(([value, label]) => ({
  value,
  label,
}))
export const statusOptions = Object.entries(statusLabels).map(([value, label]) => ({
  value,
  label,
}))

/** 生成不含模拟业务对象的新配置；每次调用返回独立实例，projectId 为 0 表示统计全部项目。 */
export function emptyTargetConfig(): MarketingTargetConfig {
  return {
    title: '',
    projectId: 0,
    ownerId: 0,
    metric: 'net_sales',
    objectType: 'employee',
    startDate: '',
    endDate: '',
    baseValue: 0,
    sprintValue: 0,
    coolingPeriodDays: 7,
    minimumSample: 10,
    objects: [],
    notification: {
      behind: true,
      stageMissed: true,
      frequency: 'daily_18',
      channels: ['system', 'wecom', 'dingtalk', 'feishu'].map((channel) => ({
        channel: channel as 'system' | 'wecom' | 'dingtalk' | 'feishu',
        enabled: channel === 'system',
        webhook: '',
        secret: '',
      })),
    },
    baseCelebration: {
      enabled: false,
      title: '{姓名}，目标达成！',
      content: '每一份努力，都在靠近更好的自己\n目标已经达成，精彩还在继续',
      imageId: 0,
    },
    sprintCelebration: {
      enabled: false,
      title: '{姓名}，冲刺成功！',
      content: '超越目标，今天的高光属于你\n冲刺成功，为全力以赴的你喝彩',
      imageId: 0,
    },
    rewards: { baseReward: '', sprintReward: '', basePenalty: '' },
  }
}
/** 返回指标最小单位到界面值的换算倍数；metric 为后端指标码。 */
export function targetValueScale(metric: MarketingTargetMetric) {
  return metric === 'net_sales' || metric === 'conversion_rate' ? 100 : 1
}
/** 按最小单位展示真实计划；value 是分、整数或基点，缺失不展示假零。 */
export function formatTargetValue(value: number | null | undefined, metric: MarketingTargetMetric) {
  if (value == null) return '—'
  const number = (value / targetValueScale(metric)).toLocaleString('zh-CN', {
    minimumFractionDigits: targetValueScale(metric) === 100 ? 2 : 0,
    maximumFractionDigits: 2,
  })
  return metric === 'conversion_rate'
    ? `${number}%`
    : metric === 'net_sales'
      ? `¥${number}`
      : number
}
/** 统一上海日期显示；value 为服务端ISO时刻，endExclusive 表示不含的周期终点。 */
export function targetDate(value: string | null, endExclusive = false) {
  if (!value) return '未设置'
  const date = new Date(new Date(value).getTime() - (endExclusive ? 1 : 0))
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date)
}

/** 展示目标实际起止时刻（上海时间，精确到分钟）；value 为服务端 ISO 时刻。 */
export function targetDateTime(value: string | null) {
  if (!value) return '未设置'
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(value))
}
